import {
  useCallback,
  useMemo,
  MouseEvent,
  useRef,
  useContext,
  useEffect,
} from 'react';
import { layers } from 'app/spacing';

import { Gradient as BaseGradient } from '__components/Kizen/ScrollFadeContainer';

import { useTranslation, TFunction } from 'react-i18next';
import { getStepConfig } from '__pages/AutomationEngine/steps';

import { useAutomationsDispatch } from 'pages/AutomationEngine/store/react';
import { actions } from 'pages/AutomationEngine/store';

import { ExecutionHistoryContext } from 'ts-components/AutomationEngine/ExecutionHistory/context';

import styled from '@emotion/styled';

import { KizenTypography } from '__app/typography';
import { format } from 'date-fns';

import DraggableStepCard from '../DraggableStepCard';
import TriggerCard from '../TriggerCard';
import StepCard from '../StepCard';
import MovingPlaceholder from '../MovingPlaceholder';

import {
  colorsSecondary,
  colorsButton,
  grayScale,
  colorsPrimary,
} from 'app/colors';

import {
  errorsToShow,
  linksToShow,
  errorByStatus,
} from 'pages/AutomationEngine/ts-helpers';
import * as cardTypes from '__pages/AutomationEngine/Engine/Steps/type';

import { ExecutionHistory, Step } from 'pages/AutomationEngine/types';
import css from '@emotion/css';
import { useVerticalScroll } from 'pages/AutomationEngine/hooks/useVerticalScroll';

const HistoryWrapper = styled.div`
  text-align: center;
  overflow-y: auto;
  max-height: ${2 * 47}px;
  min-height: ${44}px;
  position: relative;
`;

const TopGradient = styled((props) => (
  <BaseGradient fadeStart={0} fadeLength={18} {...props} />
))`
  z-index: ${layers.content(0, 1)};
  top: -2px;
`;

const BottomGradient = styled((props) => (
  <BaseGradient fadeStart={0} fadeLength={18} {...props} />
))`
  z-index: ${layers.content(0, 1)};
  bottom: -2px;
`;

const HistoryDetails = styled.div<{
  hasError?: boolean;
  focusedStep: boolean;
  rightMargin: string;
}>`
  margin-left: 8px;
  margin-right: ${({ rightMargin }) => rightMargin};
  padding: 4px 0;
  ${({ hasError, focusedStep }) =>
    focusedStep
      ? css`
          background-color: ${hasError
            ? css`rgba(255, 0, 0, 0.1)`
            : grayScale.light};
          border-radius: 4px;
        `
      : css`
          background-color: ${hasError
            ? css`rgba(255, 0, 0, 0.1)`
            : 'inherited'};
          cursor: pointer;
          :hover {
            background-color: ${hasError
              ? css`rgba(255, 0, 0, 0.2)`
              : grayScale.light};
            border-radius: 4px;
          }
        `};
`;

export const StyledErrorLink = styled(KizenTypography)`
  color: ${colorsButton.red.default};
  :hover {
    color: ${colorsButton.red.hover};
  }
  :active {
    color: ${colorsButton.red.hover};
  }
  :focus {
    color: ${colorsButton.red.hover};
  }
`;

const isVariableHistory = (history?: ExecutionHistory) =>
  Boolean(history?.detailedLog?.variableName);

export const historyStatus = (t: TFunction) => ({
  pending: t(`Pending`),
  skipped: t(`Paused`),
  completed: t(`Completed`),
  failed: t(`Failed`),
  paused_by_failure: t(`Paused by failure`),
  pending_throttled: t(`Pending (Throttled)`),
});

type HistoryPanelProps = {
  historyList: ExecutionHistory[];
  step: Step;
};

type CardType = {
  type: string;
  title: string;
  color: string;
  stats?: object;
  movable: boolean;
  creatable: boolean;
  deletable: boolean;
  duplicatable: boolean;
  copyable: boolean;
  goalType: boolean;
  prefix: string;
  CardComponent: string;
  latestDuplicate: any;
  showErrors: boolean;
};

export const historyColors = {
  pending: colorsPrimary.orange.dark,
  skipped: colorsPrimary.orange.dark,
  completed: colorsPrimary.green.dark,
  failed: colorsSecondary.red.dark,
  paused_by_failure: colorsSecondary.red.dark,
  pending_throttled: colorsPrimary.orange.dark,
  variable: colorsSecondary.teal.dark,
};

export const getHistoryColor = (history?: ExecutionHistory) =>
  isVariableHistory(history)
    ? historyColors.variable
    : (history?.status && historyColors[history.status]) || grayScale.light;

type UseExecutionHistory = {
  step: any;
  type: string;
  showMovingPlaceholder: boolean;
  onStartProp?: any;
  onStopProp?: any;
  scaleProp?: any;
};

export const useExecutionHistory = ({
  step,
  type,
  showMovingPlaceholder,
  onStartProp,
  onStopProp,
  scaleProp,
}: UseExecutionHistory) => {
  const { t } = useTranslation();
  const stepType = step.type === 'goal' ? step.details.type : step.type;

  const stepConfig: CardType = getStepConfig(stepType, {
    isTrigger: type === 'automationTrigger',
  });

  const { historyId, getSelectedHistoryStep, showHistorySideBar } = useContext(
    ExecutionHistoryContext
  );

  const selectedHistoryStep = getSelectedHistoryStep();

  const {
    color,
    borderColor,
    CardComponent,
    menuOptions,
    historyMode,
    addMenu,
    unusedHistoryStep,
    focusedHistoryStep,
    scale,
    onStart,
    onStop,
    showErrors,
  } = useMemo(() => {
    if (historyId) {
      const showErrors =
        errorByStatus(step?.status) || (step?.history || []).some(errorsToShow);

      const color = showErrors
        ? historyColors.failed
        : getHistoryColor(step.history?.[0]);

      const isSelectedStep =
        showHistorySideBar &&
        selectedHistoryStep &&
        (step.id === selectedHistoryStep.stepId ||
          step.id === selectedHistoryStep.triggerId);

      return {
        color: color,
        borderColor: color,
        CardComponent:
          cardTypes[stepConfig.CardComponent] ||
          (type === 'automationTrigger' && TriggerCard) ||
          StepCard,
        menuOptions: [],
        historyMode: true,
        addMenu: false,
        unusedHistoryStep: !step.history,
        focusedHistoryStep: isSelectedStep,
        scale: null,
        onStart: null,
        onStop: null,
        showErrors: !!showErrors,
      };
    }

    return {
      color: step.hasError ? colorsButton.red.hover : stepConfig.color,
      borderColor: step.hasError ? colorsButton.red.hover : null,
      CardComponent:
        (showMovingPlaceholder && MovingPlaceholder) ||
        cardTypes[stepConfig.CardComponent] ||
        (type === 'automationTrigger' && TriggerCard) ||
        (stepConfig.movable && DraggableStepCard) ||
        StepCard,
      menuOptions: [
        stepConfig.creatable && { label: t('Edit'), value: 'edit' },
        stepConfig.deletable && { label: t('Delete'), value: 'delete' },
        stepConfig.duplicatable && {
          label: t('Duplicate'),
          value: 'duplicate-step',
        },
        stepConfig.copyable && {
          label: t('Copy'),
          value: 'copy-step',
        },
      ].filter(Boolean),
      historyMode: false,
      addMenu: true,
      unusedHistoryStep: false,
      focusedHistoryStep: false,
      scale: scaleProp,
      onStart: onStartProp,
      onStop: onStopProp,
      showErrors: step.hasError,
    };
  }, [
    historyId,
    step.hasError,
    step.history,
    step?.status,
    step.id,
    stepConfig.color,
    stepConfig.CardComponent,
    stepConfig.movable,
    stepConfig.creatable,
    stepConfig.deletable,
    stepConfig.duplicatable,
    stepConfig.copyable,
    showMovingPlaceholder,
    type,
    t,
    scaleProp,
    onStartProp,
    onStopProp,
    showHistorySideBar,
    selectedHistoryStep,
  ]);

  return {
    historyId,
    color,
    borderColor,
    CardComponent,
    menuOptions,
    historyMode,
    addMenu,
    unusedHistoryStep,
    focusedHistoryStep,
    scale,
    onStart,
    onStop,
    showErrors,
  };
};

export const statusDecoration = (
  history: ExecutionHistory,
  step: Step | undefined | null,
  t: TFunction
) => {
  if (history.stepOutput === null || !step) {
    return history.stepOutput;
  }

  if (step.type === 'goal') {
    return history.stepOutput ? t(' (Met Path)') : t(' (Not Met Path)');
  }
  return history.stepOutput ? t(' (Yes Path)') : t(' (No Path)');
};

export const HistoryPanel = ({ historyList, step }: HistoryPanelProps) => {
  const { t } = useTranslation();
  const translatedStatus = useMemo(() => historyStatus(t), [t]);
  const dispatch = useAutomationsDispatch();
  const refs = useRef<Record<string, HTMLDivElement | null>>({});
  const { containerProps, topFade, bottomFade, scrollDivRef } =
    useVerticalScroll([historyList, step]);

  const { selectedHistoryStepIndex, setSelectedHistoryStepIndex } = useContext(
    ExecutionHistoryContext
  );

  const handleShowError = useCallback(
    (event: MouseEvent<HTMLButtonElement>, history: ExecutionHistory) => {
      event.preventDefault();
      dispatch(actions.showHistoryErrorModal([history]));
    },
    [dispatch]
  );

  const handleFocusStep = useCallback(
    (history: ExecutionHistory) => {
      setSelectedHistoryStepIndex(history.index);
      dispatch(actions.setFocusStep({ step: step.id }));
    },
    [dispatch, setSelectedHistoryStepIndex, step]
  );

  // beacuse react-flow is taking over the scroll event
  const handleWheel = (event: any) => {
    event.preventDefault();
    const top = scrollDivRef.current?.scrollTop + event.deltaY;
    scrollDivRef.current?.scrollBy({ top, behavior: 'smooth' });
  };

  useEffect(() => {
    if (
      selectedHistoryStepIndex >= 0 &&
      refs.current[selectedHistoryStepIndex]?.offsetTop
    ) {
      const top = refs.current[selectedHistoryStepIndex]?.offsetTop;

      scrollDivRef.current?.scrollTo({ top, behavior: 'smooth' });
    }
  }, [scrollDivRef, selectedHistoryStepIndex]);

  const rightMargin = historyList.length < 3 ? '8px' : '0';

  return (
    <HistoryWrapper
      className="kds-expanding-scrollbar"
      onWheel={handleWheel}
      {...containerProps}
    >
      <TopGradient position="top" fadeIn={topFade} />
      {historyList.map((history) => {
        const hasError = errorByStatus(history.status) || errorsToShow(history);
        const hasLink = linksToShow(history);

        const status = translatedStatus[history.status] || history.status;

        const stepOutput = statusDecoration(history, step, t);

        const statusColor = hasError
          ? historyColors.failed
          : getHistoryColor(history);

        return (
          <HistoryDetails
            key={history.id}
            ref={(el) => (refs.current[history.index] = el)}
            hasError={!!hasError}
            focusedStep={selectedHistoryStepIndex === history.index}
            rightMargin={rightMargin}
            onClick={() => handleFocusStep(history)}
          >
            <div>
              <KizenTypography as="span" size="button">
                {t('Order of execution: ')}
              </KizenTypography>
              <KizenTypography as="span" size="button" weight="bold">
                {history.index + 1}
              </KizenTypography>
            </div>
            <div>
              <KizenTypography as="span" size="button">
                {t('Status: ')}
              </KizenTypography>
              {hasError ? (
                <StyledErrorLink
                  as="span"
                  size="button"
                  weight="bold"
                  type="link"
                  onClick={(event) => handleShowError(event, history)}
                >
                  {status}
                  {stepOutput}
                </StyledErrorLink>
              ) : hasLink ? (
                <KizenTypography
                  as="span"
                  size="button"
                  weight="bold"
                  type="link"
                  color={statusColor}
                  onClick={(event) => handleShowError(event, history)}
                >
                  {status}
                  {stepOutput}
                </KizenTypography>
              ) : (
                <KizenTypography
                  as="span"
                  size="button"
                  weight="bold"
                  color={statusColor}
                >
                  {status}
                  {stepOutput}
                </KizenTypography>
              )}
            </div>

            <div>
              <KizenTypography as="span" size="button">
                {t('Time: ')}
              </KizenTypography>
              <KizenTypography as="span" size="button" weight="bold">
                {format(
                  history.updated || history.created,
                  'MM/dd/yyyy HH:mm:ss'
                )}
              </KizenTypography>
            </div>
          </HistoryDetails>
        );
      })}
      <BottomGradient position="bottom" fadeIn={bottomFade} />
    </HistoryWrapper>
  );
};
